/*
 * fixtureAdapter — implements the DataAdapter contract against local demo data.
 * Reads resolve from fixtures; the check-in write is recorded into an in-memory
 * + localStorage log so the whole flow is exercisable with no backend.
 */
import { TENANT, PROJECTS, EMPLOYEES, ASSIGNMENTS, IS_DEMO } from './fixtures.js'
import { todayISO, isoDate } from './workdate.js'
import {
  buildCheckInRecords, buildPhotoEvidence, buildIssuePending,
  buildCheckOutRecords, buildDayCloseRecords, CONFIDENCE,
} from '../../shared/captureMapping.js'

const WRITE_LOG_KEY = 'flow.fixture.writeLog'

// BUILD 001 — two demo stores that must survive a page reload, because the
// owner board and the worker phone are the same browser during testing. An
// assignment saved by the owner has to still be there when the worker opens
// Flow, and a check-in has to still show on the board after a refresh.
const ASSIGN_KEY = 'flow.fixture.assignments'   // owner-created Project Assignments
const WORKDAY_KEY = 'flow.fixture.workdays'     // `${employeeId}|${date}` → workday facts
const REPORT_KEY = 'flow.fixture.reports'       // `${employeeId}|${date}` → Daily Report draft
const DETAIL_KEY = 'flow.fixture.assignmentDetails' // assignmentId → work detail (Build 003)

// Field-created (Pending Approval) demo projects — session-scoped, honest demo.
const createdProjects = []

// Demo mirror of the One Active Workday guard: employeeId → active session.
const activeWorking = new Map()

function readLog() {
  try { return JSON.parse(localStorage.getItem(WRITE_LOG_KEY) || '[]') } catch { return [] }
}
function appendLog(entry) {
  const log = readLog(); log.push(entry)
  try { localStorage.setItem(WRITE_LOG_KEY, JSON.stringify(log)) } catch { /* ignore quota */ }
}

function readJSON(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)) } catch { return fallback }
}
function writeJSON(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* ignore quota */ }
}

/** Seed fixtures plus anything the owner has assigned in this browser. */
function allAssignments() {
  return [...ASSIGNMENTS, ...readJSON(ASSIGN_KEY, [])]
}

/*
 * BUILD 003 — ASSIGNMENT WORK DETAIL, deliberately a SEPARATE object.
 *
 * The dated Assignment stays exactly what it is: a placement — worker, project,
 * date. Title, instructions and photos are work DETAIL, and they are kept in
 * their own record keyed by assignment id rather than bolted onto the
 * placement. Two reasons, and the second is the one that matters:
 *
 *   - Placement and work detail have different lifetimes. An Assignment can be
 *     voided while its content must survive, which is awkward if the content
 *     lives on the record being withdrawn.
 *   - This detail object is a TEMPORARY STAND-IN for future Work Item
 *     semantics, which are excluded from Alpha. Keeping it separate means Work
 *     Item can replace it later without migrating the placement record, and
 *     means nothing here quietly becomes canon. Recorded as a Build 003
 *     assumption in the report and Upgrade Register.
 *
 * Voiding NEVER erases. A voided detail keeps its title, instructions, photo
 * references and history; it simply stops being active.
 */
function readDetails() { return readJSON(DETAIL_KEY, {}) }

function getDetail(assignmentId) {
  return readDetails()[assignmentId] ?? null
}

/** Active means: no detail record at all (a bare placement), or not voided. */
function isVoided(assignmentId) {
  return getDetail(assignmentId)?.status === 'voided'
}

function putDetail(assignmentId, patch, action) {
  const all = readDetails()
  const now = new Date().toISOString()
  const prev = all[assignmentId] ?? {
    assignmentId, title: '', description: '', photoIds: [],
    status: 'active', createdAt: now, updatedAt: now, voidedAt: null, history: [],
  }
  // What actually changed — recorded, so an edit is auditable rather than a
  // silent overwrite.
  const changed = Object.keys(patch).filter((k) => {
    if (k === 'photoIds') return JSON.stringify(patch[k]) !== JSON.stringify(prev.photoIds)
    return patch[k] !== prev[k]
  })
  const next = {
    ...prev, ...patch, assignmentId, updatedAt: now,
    history: [...prev.history, { at: now, action, changed }],
  }
  all[assignmentId] = next
  writeJSON(DETAIL_KEY, all)
  return next
}

function readWorkdays() { return readJSON(WORKDAY_KEY, {}) }

/*
 * BUILD 002 — U-4: a workday belongs to a SUBJECT, which is either a matched
 * Employee or an UNMATCHED TYPED NAME.
 *
 * Build 001 keyed workdays on an Employee id and wrote nothing when there was
 * none, so a person who typed their name because the roster did not list them
 * vanished from Workforce Today entirely. The owner could not see that someone
 * unidentified had reported being on site — the single case they most need to
 * see.
 *
 * The fix is NOT to invent an Employee id. An unmatched capture keys into its
 * own namespace, derived from the name the person actually typed, so it can
 * never collide with a real Employee's day or be silently folded into one.
 * Identity stays unresolved, exactly as captured; only visibility changes.
 */
function subjectOf(session) {
  const id = session?.employee?.id ?? null
  if (id) return { employeeId: id, typedName: null }
  const typed = String(session?.employee?.name ?? '').trim()
  return { employeeId: null, typedName: typed || null }
}

function workdayKey(subject, date) {
  if (subject.employeeId) return `${subject.employeeId}|${date}`
  // A separate namespace — never an Employee id, real or manufactured.
  return `unmatched:${String(subject.typedName ?? '').toLowerCase()}|${date}`
}

/*
 * BUILD 001 — a workday is a LIST OF SEGMENTS, never a single pair of times.
 *
 * The first shape stored one checkedInAt/checkedOutAt per employee+date. A
 * re-check-in then had to overwrite checkedInAt, which destroyed the original
 * check-out — exactly the fact State B (Locked Founder Decision) exists to
 * preserve. Segments make that structurally impossible: a re-check-in APPENDS,
 * and no earlier fact is ever rewritten.
 *
 * Status and hours are DERIVED from the segments (see summarize) and never
 * stored, so a stored status can never contradict the recorded events.
 */
function normalizeWorkday(rec) {
  if (!rec) return null
  if (Array.isArray(rec.segments)) return { typedName: null, ...rec }
  // Migrate a record written by the earlier flat shape — no data is dropped.
  return {
    employeeId: rec.employeeId ?? null,
    typedName: rec.typedName ?? null,
    date: rec.date,
    segments: rec.checkedInAt ? [{
      recordId: rec.recordId ?? null,
      checkedInAt: rec.checkedInAt,
      checkedOutAt: rec.checkedOutAt ?? null,
      reportedProjectId: rec.reportedProjectId ?? null,
      reportedProjectName: rec.reportedProjectName ?? null,
      held: !!rec.held,
      reason: null,
    }] : [],
  }
}

function getWorkday(subject, date) {
  return normalizeWorkday(readWorkdays()[workdayKey(subject, date)])
}

function saveWorkday(rec) {
  const all = readWorkdays()
  all[workdayKey(rec, rec.date)] = rec
  writeJSON(WORKDAY_KEY, all)
  return rec
}

/** Append a new working segment. Earlier segments are never touched. */
function openSegment(subject, date, segment) {
  const rec = getWorkday(subject, date)
    || { employeeId: subject.employeeId, typedName: subject.typedName, date, segments: [] }
  rec.segments.push({ checkedOutAt: null, reason: null, ...segment })
  return saveWorkday(rec)
}

/** Close the most recent OPEN segment. A closed one stays closed, forever. */
function closeSegment(subject, date, checkedOutAtISO) {
  const rec = getWorkday(subject, date)
  if (!rec) return null
  for (let i = rec.segments.length - 1; i >= 0; i -= 1) {
    if (!rec.segments[i].checkedOutAt) {
      rec.segments[i] = { ...rec.segments[i], checkedOutAt: checkedOutAtISO }
      break
    }
  }
  return saveWorkday(rec)
}

/*
 * Derived view of a workday. Hours for Build 001 = the sum of every completed
 * segment (check-out minus check-in), so a re-check-in adds its own time
 * rather than silently stretching the first one.
 */
function summarize(rec) {
  const segs = rec?.segments ?? []
  if (segs.length === 0) {
    return { status: 'not-in', checkedInAt: null, checkedOutAt: null, minutes: 0, segments: [], held: false }
  }
  const open = segs.some((s) => !s.checkedOutAt)
  let minutes = 0
  for (const s of segs) {
    if (s.checkedInAt && s.checkedOutAt) {
      minutes += Math.max(0, (new Date(s.checkedOutAt) - new Date(s.checkedInAt)) / 60000)
    }
  }
  return {
    status: open ? 'working' : 'checked-out',
    checkedInAt: segs[0].checkedInAt,                                 // first arrival of the day
    checkedOutAt: open ? null : segs[segs.length - 1].checkedOutAt,   // last departure
    minutes: Math.round(minutes),
    segments: segs,
    held: segs.some((s) => s.held),
  }
}

/*
 * BUILD 001 — Daily Report drafts (Figma 5:50).
 *
 * 5:96 states the rule the screen must honour: "Reports are saved
 * automatically." A draft is therefore persisted on every change, not on
 * submit, so closing the app never loses what someone typed. Submitting only
 * stamps submittedAt — it never clears the text, because the text IS the
 * record.
 */
function reportKey(employeeId, date) { return `${employeeId}|${date}` }

function getReport(employeeId, date) {
  const all = readJSON(REPORT_KEY, {})
  return all[reportKey(employeeId, date)]
    || { employeeId, date, text: '', photos: [], submittedAt: null, updatedAt: null }
}

function putReport(employeeId, date, patch) {
  const all = readJSON(REPORT_KEY, {})
  const key = reportKey(employeeId, date)
  all[key] = {
    ...getReport(employeeId, date), ...patch,
    employeeId, date, updatedAt: new Date().toISOString(),
  }
  writeJSON(REPORT_KEY, all)
  return all[key]
}

/*
 * BUILD 003 — a placement plus its work detail, as the Owner list needs it.
 * Seeded fixture assignments have no detail record; they decorate to empty
 * strings rather than being hidden, because a bare placement is still valid.
 */
function decorateAssignment(placement) {
  const project = projectById(placement.projectId)
  const employee = EMPLOYEES.find((e) => e.id === placement.employeeId)
  const detail = getDetail(placement.id)
  return {
    assignmentId: placement.id,
    employeeId: placement.employeeId,
    employeeName: employee?.name ?? 'Unknown',
    projectId: placement.projectId,
    projectName: project?.name ?? 'Unknown project',
    projectLocation: project?.location ?? '',
    workDate: placement.workDate ?? null,
    standing: !placement.workDate,
    title: detail?.title ?? '',
    description: detail?.description ?? '',
    photoIds: detail?.photoIds ?? [],
    voided: detail?.status === 'voided',
    voidedAt: detail?.voidedAt ?? null,
    createdAt: detail?.createdAt ?? null,
    updatedAt: detail?.updatedAt ?? null,
    history: detail?.history ?? [],
  }
}

function projectById(id) {
  return PROJECTS.find((p) => p.id === id)
    || createdProjects.find((p) => p.id === id)
    || null
}

// Rehydrate the One Active Workday guard from persisted demo state, so a page
// reload does not make a working day look available for a second check-in.
;(function rehydrateActive() {
  const today = todayISO()
  for (const raw of Object.values(readWorkdays())) {
    const rec = normalizeWorkday(raw)
    if (rec?.date !== today) continue
    // The One Active Workday guard keys on an Employee id; an unmatched capture
    // has none and is held for the Pending Desk, which skips the guard anyway.
    if (!rec.employeeId) continue
    const open = rec.segments.find((sg) => !sg.checkedOutAt)
    if (open) {
      activeWorking.set(rec.employeeId, {
        recordId: open.recordId,
        projectName: open.reportedProjectName ?? null,
        checkedInAt: open.checkedInAt,
      })
    }
  }
})()

export function createFixtureAdapter() {
  return {
    isDemo: IS_DEMO,
    mode: 'fixture',

    async resolveTenant(code) {
      // Normalize before lookup: trim + case-fold. Unknown codes still fail.
      const clean = normalizeCode(code)
      if (clean && clean === normalizeCode(TENANT.code)) {
        return { id: TENANT.id, name: TENANT.name }
      }
      // A QR payload carries the tenant id directly (exact, not normalized).
      const exact = String(code || '').trim()
      if (exact && (exact === TENANT.id || TENANT.aliases?.includes(exact))) {
        return { id: TENANT.id, name: TENANT.name }
      }
      const err = new Error('Tenant not found')
      err.code = 'TENANT_NOT_FOUND'
      throw err
    },

    async listTeamMembers(/* tenantId */) {
      // Check-In eligibility per the CP-001 addendum applying DB-6.4: an
      // Active Assignment is the context — no role filter. Mirrors the live
      // adapter exactly (Test Worker Six has no assignment → correctly absent).
      const assignedIds = new Set(
        ASSIGNMENTS.filter((a) => a.status === 'Active').map((a) => a.employeeId),
      )
      return EMPLOYEES
        .filter((e) => e.active && assignedIds.has(e.id))
        .map(({ id, name, role, active }) => ({ id, name, role, active }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    async resolveAssignment(employeeId) {
      const active = ASSIGNMENTS.filter((a) => a.employeeId === employeeId && a.status === 'Active')
      if (active.length === 0) {
        return { project: null, confidence: CONFIDENCE.LOW, assignmentId: null, ambiguous: false }
      }
      if (active.length > 1) {
        // Multiple active assignments = genuine ambiguity → medium, needs a tap.
        const project = PROJECTS.find((p) => p.id === active[0].projectId) || null
        return { project, confidence: CONFIDENCE.MEDIUM, assignmentId: active[0].id, ambiguous: true }
      }
      const project = PROJECTS.find((p) => p.id === active[0].projectId) || null
      return { project, confidence: CONFIDENCE.HIGH, assignmentId: active[0].id, ambiguous: false }
    },

    /*
     * BUILD 001 — dated Project Assignment lookup.
     *
     * Deliberately separate from resolveAssignment, which carries the
     * confidence value into Pending Desk routing. This one answers a narrower
     * question — "where is this person expected on this date?" — and returns
     * no confidence value, so nothing downstream changes.
     *
     * Only DATED assignments answer. A standing assignment (no workDate) is
     * roster context, never today's expectation.
     */
    async getAssignmentForDate(employeeId, dateISO) {
      const hit = allAssignments().find(
        (a) => a.employeeId === employeeId
          && a.status === 'Active'
          && a.workDate === dateISO
          // BUILD 003: a voided Assignment leaves the worker's active view.
          // It is withdrawn as an expectation — never erased.
          && !isVoided(a.id),
      )
      if (!hit) return null
      const project = projectById(hit.projectId)
      if (!project) return null
      const detail = getDetail(hit.id)
      return {
        assignmentId: hit.id,
        workDate: hit.workDate,
        project: { id: project.id, name: project.name, location: project.location || '' },
        // BUILD 003 work detail. Absent on a bare placement, which is valid.
        title: detail?.title || '',
        description: detail?.description || '',
        photoIds: detail?.photoIds ?? [],
      }
    },

    /*
     * BUILD 001 — this worker's own day, for the Workday timeline (Figma 4:2).
     * Returns the derived summary plus every segment, so the screen renders
     * recorded events and never invents one.
     */
    async getWorkdayForDate(employeeId, dateISO, typedName = null) {
      return summarize(getWorkday({ employeeId: employeeId ?? null, typedName }, dateISO))
    },

    /* BUILD 001 — Daily Report (Figma 5:50). Autosaved draft for one day. */
    async getDailyReport(employeeId, dateISO) {
      return getReport(employeeId, dateISO)
    },

    /** Autosave. Called on every change — never on a button alone. */
    async saveDailyReport(employeeId, dateISO, patch) {
      return putReport(employeeId, dateISO, patch)
    },

    /*
     * Submit stamps the report as submitted and logs it. It does NOT clear or
     * lock the text: the worker can still correct a report they just sent, and
     * the office sees the latest version rather than a truncated one.
     */
    async submitDailyReport(session, dateISO, capturedAtISO) {
      const employeeId = session.employee?.id ?? null
      if (!employeeId) return { success: false, reason: 'NO_EMPLOYEE' }
      const rec = putReport(employeeId, dateISO, { submittedAt: capturedAtISO })
      const recordId = `fixture://report/${cryptoRandom()}`
      appendLog({ recordId, at: capturedAtISO, records: {
        type: 'daily-report',
        employeeId,
        projectId: session.project?.id ?? null,
        workDate: dateISO,
        text: rec.text,
        photoCount: rec.photos.length,
      } })
      return { success: true, recordId, submittedAt: capturedAtISO, syncStatus: 'saved' }
    },

    /*
     * BUILD 001 — State E, Workday Review Required (Figma 9:2).
     *
     * A workday on a PAST date that still has an open segment: Flow recorded a
     * check-in and no check-out. Today's open segment is simply someone still
     * working, so it is never included.
     *
     * Nothing here closes the day. No check-out is manufactured, ever — the
     * governance caveat on 9:48 is the behaviour, not a caption.
     */
    async listWorkdaysNeedingReview(employeeId, todayDateISO) {
      const out = []
      for (const raw of Object.values(readWorkdays())) {
        const rec = normalizeWorkday(raw)
        if (!rec || rec.employeeId !== employeeId) continue
        if (rec.date >= todayDateISO) continue
        const open = rec.segments.find((sg) => !sg.checkedOutAt)
        if (!open) continue
        out.push({
          date: rec.date,
          checkedInAt: open.checkedInAt,
          reportedProjectName: open.reportedProjectName ?? null,
          employeeReport: rec.employeeReport ?? null,
        })
      }
      return out.sort((a, b) => a.date.localeCompare(b.date))
    },

    /*
     * The employee's account of an unclosed day. Stored ALONGSIDE the recorded
     * events as a separate fact — never merged into them, and never converted
     * into a checkedOutAt. Both records stand; an authorized reviewer resolves
     * the difference, not this screen.
     */
    async submitWorkdayReview(employeeId, dateISO, text, submittedAtISO, typedName = null) {
      const rec = getWorkday({ employeeId: employeeId ?? null, typedName }, dateISO)
      if (!rec) return { success: false, reason: 'NO_WORKDAY' }
      rec.employeeReport = { text, submittedAt: submittedAtISO }
      saveWorkday(rec)
      const recordId = `fixture://review/${cryptoRandom()}`
      appendLog({ recordId, at: submittedAtISO, records: {
        type: 'workday-review',
        employeeId, workDate: dateISO, text,
        checkOutManufactured: false,
      } })
      return { success: true, recordId, submittedAt: submittedAtISO, syncStatus: 'saved' }
    },

    /*
     * BUILD 001 — the Owner's Workforce board for one date.
     *
     * Rows are the UNION of (a) everyone assigned that date and (b) everyone
     * who actually has a workday that date. (b) matters: a worker who checked
     * in without an assignment must appear, or the board quietly hides exactly
     * the case the owner most needs to see.
     *
     * Assigned project and reported project are carried SEPARATELY and never
     * collapsed — Independent Facts Principle. Where they differ, both show.
     */
    async listWorkforceForDate(tenantId, dateISO) {
      const days = readWorkdays()
      const assignedFor = allAssignments().filter(
        // BUILD 003: a voided Assignment leaves the Owner's active view too.
        // Any workday FACT already captured against it is untouched — the
        // expectation is withdrawn, the record of what happened is not.
        (a) => a.status === 'Active' && a.workDate === dateISO && !isVoided(a.id),
      )

      const ids = new Set(assignedFor.map((a) => a.employeeId))
      // BUILD 002 / U-4 — unmatched captures are collected separately: they
      // have no Employee id to key on, and inventing one is exactly what must
      // not happen. They become their own rows below.
      const unmatched = []
      for (const raw of Object.values(days)) {
        const rec = normalizeWorkday(raw)
        if (rec?.date !== dateISO) continue
        if (rec.employeeId) ids.add(rec.employeeId)
        else if (rec.typedName) unmatched.push(rec)
      }

      return [...ids].map((employeeId) => {
        const employee = EMPLOYEES.find((e) => e.id === employeeId)
        const assignment = assignedFor.find((a) => a.employeeId === employeeId)
        const assignedProject = assignment ? projectById(assignment.projectId) : null
        const day = summarize(normalizeWorkday(days[workdayKey({ employeeId, typedName: null }, dateISO)]))

        // Every DISTINCT project the worker actually reported today. Usually
        // one; a re-check-in elsewhere makes it two, and the owner sees both
        // rather than only the latest.
        const reported = [...new Set(
          day.segments.map((sg) => sg.reportedProjectName).filter(Boolean),
        )]

        // A past date with an open segment is a day Flow never saw end.
        const needsReview = dateISO < todayISO() && day.segments.some((sg) => !sg.checkedOutAt)

        return {
          employeeId,
          employeeName: employee?.name ?? 'Unknown',
          unmatched: false,
          needsReview,
          employeeReport: normalizeWorkday(days[workdayKey({ employeeId, typedName: null }, dateISO)])?.employeeReport ?? null,
          assignedProjectName: assignedProject?.name ?? null,
          reportedProjectName: reported.length ? reported[reported.length - 1] : null,
          reportedProjectNames: reported,
          /*
           * 'working' means someone is on the clock right now. An open segment
           * on a PAST date is not that — it is a day Flow never saw end, and
           * showing it as WORKING would tell the owner a crew is on site when
           * nobody is. It reports as 'review' instead.
           */
          status: needsReview ? 'review' : day.status,  // working | checked-out | review | not-in
          checkedInAt: day.checkedInAt,
          checkedOutAt: day.checkedOutAt,
          minutes: day.minutes,                 // hours = check-out minus check-in, summed
          segments: day.segments,
          held: day.held,
        }
      }).concat(unmatched.map((rec) => {
        /*
         * An unmatched person's row. The typed name is carried through EXACTLY
         * as entered — not normalised, not title-cased, not matched against
         * anyone. employeeId stays null: no identity is manufactured here, and
         * the office resolves it through the existing Pending Desk path.
         *
         * assignedProjectName is null because an unidentified person has no
         * Assignment to expect them anywhere. The reported project is a fact
         * they supplied, and it is preserved as one.
         */
        const day = summarize(rec)
        const reported = [...new Set(
          day.segments.map((sg) => sg.reportedProjectName).filter(Boolean),
        )]
        return {
          employeeId: null,
          employeeName: rec.typedName,
          unmatched: true,
          needsReview: false,
          employeeReport: rec.employeeReport ?? null,
          assignedProjectName: null,
          reportedProjectName: reported.length ? reported[reported.length - 1] : null,
          reportedProjectNames: reported,
          status: day.status,
          checkedInAt: day.checkedInAt,
          checkedOutAt: day.checkedOutAt,
          minutes: day.minutes,
          segments: day.segments,
          held: day.held,
        }
      })).sort((a, b) => a.employeeName.localeCompare(b.employeeName))
    },

    /*
     * BUILD 001 — the owner assigns a worker to a project on a date.
     * Re-assigning the same employee+date REPLACES that date's assignment
     * rather than stacking a second one, so the board can never show two
     * expectations for one person on one day.
     */
    async saveAssignment({ employeeId, projectId, workDate, title, description, photoIds }) {
      const created = readJSON(ASSIGN_KEY, [])
      /*
       * One expectation per worker per day. Saving again for the same pair
       * REUSES the existing record id rather than minting a new one, so the
       * work detail hanging off it follows the edit instead of being orphaned.
       * A voided assignment is skipped, so re-assigning after a void starts a
       * genuinely new one and leaves the voided history intact.
       */
      const existing = created.find(
        (a) => a.employeeId === employeeId && a.workDate === workDate && !isVoided(a.id),
      )
      const record = existing
        ? { ...existing, projectId, status: 'Active' }
        : { id: `fixture://assign/new-${cryptoRandom()}`, employeeId, projectId, status: 'Active', workDate }

      const kept = created.filter((a) => a.id !== record.id)
      kept.push(record)
      writeJSON(ASSIGN_KEY, kept)

      // Work detail is written only when the caller supplied any, so a bare
      // placement stays bare.
      const detailPatch = {}
      if (title !== undefined) detailPatch.title = title
      if (description !== undefined) detailPatch.description = description
      if (photoIds !== undefined) detailPatch.photoIds = photoIds
      let detail = getDetail(record.id)
      if (Object.keys(detailPatch).length) {
        detail = putDetail(record.id, detailPatch, existing ? 'edited' : 'created')
      }

      appendLog({ recordId: record.id, records: { type: 'assignment', ...record, ...detailPatch } })
      return { ...record, detail }
    },

    /* BUILD 003 — one Assignment with its work detail. */
    async getAssignment(assignmentId) {
      const placement = allAssignments().find((a) => a.id === assignmentId)
      if (!placement) return null
      return decorateAssignment(placement)
    },

    /*
     * BUILD 003 — the Owner's Assignments list.
     *
     * Active and voided are returned SEPARATELY rather than as one list with a
     * flag, because the whole point of voiding is that the two must not be
     * confused: voided work is history, and history must not read as something
     * a crew is expected to do.
     */
    async listAssignments(/* tenantId */) {
      const all = allAssignments()
        .filter((a) => a.status === 'Active')
        .map(decorateAssignment)
        .filter(Boolean)
      const byDateThenWorker = (x, y) =>
        (y.workDate ?? '').localeCompare(x.workDate ?? '') || x.employeeName.localeCompare(y.employeeName)
      return {
        active: all.filter((a) => !a.voided).sort(byDateThenWorker),
        voided: all.filter((a) => a.voided).sort(byDateThenWorker),
      }
    },

    /*
     * BUILD 003 — edit the work detail. This can never touch the placement,
     * and can never touch a captured fact: it writes to the detail store only.
     */
    async updateAssignmentDetail(assignmentId, patch) {
      const placement = allAssignments().find((a) => a.id === assignmentId)
      if (!placement) return { success: false, reason: 'NO_ASSIGNMENT' }
      const clean = {}
      if (patch.title !== undefined) clean.title = patch.title
      if (patch.description !== undefined) clean.description = patch.description
      if (patch.photoIds !== undefined) clean.photoIds = patch.photoIds
      const detail = putDetail(assignmentId, clean, 'edited')
      appendLog({ recordId: assignmentId, records: { type: 'assignment-edit', assignmentId, ...clean } })
      return { success: true, detail }
    },

    /*
     * BUILD 003 — VOID, which the Owner's button calls Delete.
     *
     * Nothing is erased. The placement record stays, the title, instructions,
     * photo references and history all stay, and the workday facts captured
     * against this Assignment are not so much as read here. The record simply
     * stops being an active expectation.
     */
    async voidAssignment(assignmentId, reason = null) {
      const placement = allAssignments().find((a) => a.id === assignmentId)
      if (!placement) return { success: false, reason: 'NO_ASSIGNMENT' }
      if (isVoided(assignmentId)) return { success: true, alreadyVoided: true, detail: getDetail(assignmentId) }
      const detail = putDetail(
        assignmentId,
        { status: 'voided', voidedAt: new Date().toISOString(), voidReason: reason },
        'voided',
      )
      appendLog({ recordId: assignmentId, records: { type: 'assignment-void', assignmentId, reason, erased: false } })
      return { success: true, detail }
    },

    /** Everyone in the tenant — the owner picks from this, not from the roster gate. */
    async listEmployees(/* tenantId */) {
      return EMPLOYEES.filter((e) => e.active)
        .map(({ id, name, role }) => ({ id, name, role }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    async listProjects(/* tenantId */) {
      // Approved list + any field-created (pending) projects this session.
      return [
        ...PROJECTS.filter((p) => p.status === 'Active')
          .map(({ id, name, location }) => ({ id, name, location, pendingApproval: false })),
        ...createdProjects,
      ]
    },

    async createProject(tenantId, name /*, actorId */) {
      const project = {
        id: `fixture://project/new-${cryptoRandom()}`,
        name,
        location: '',
        pendingApproval: true,
      }
      createdProjects.push(project)
      appendLog({ recordId: project.id, records: { type: 'project', name, status: 'Pending Approval' } })
      return project
    },

    async submitCheckIn(session, capturedAtISO) {
      const records = buildCheckInRecords(session, capturedAtISO)
      // Mirror of the live One Active Workday guard (demo fidelity: the
      // duplicate-rejection UI is testable without the backend).
      if (!records.held) {
        const active = activeWorking.get(session.employee.id)
        if (active) {
          return {
            success: false,
            duplicate: true,
            recordId: active.recordId,
            checkedInAt: active.checkedInAt,
            activeSession: { ...active },
            syncStatus: 'rejected',
          }
        }
      }
      const recordId = `fixture://labor/${cryptoRandom()}`
      appendLog({ recordId, at: capturedAtISO, records })
      if (!records.held) {
        activeWorking.set(session.employee.id, {
          recordId,
          projectName: session.project?.name ?? null,
          checkedInAt: capturedAtISO,
        })
      }
      // BUILD 001 — record the workday fact so the Owner board can see it.
      // The REPORTED project is what the worker said; the assignment is not
      // touched here and is read separately. Both facts, never merged.
      /*
       * BUILD 002 / U-4 — record the workday fact unconditionally.
       *
       * This was gated on `session.employee?.id`, so an unmatched typed-name
       * capture wrote nothing and never reached Workforce Today. Unresolved
       * identity is not a reason to lose a capture: the person reported being
       * on site, and that is a fact whether or not Flow knows who they are.
       */
      {
        openSegment(subjectOf(session), isoDate(new Date(capturedAtISO)), {
          recordId,
          reportedProjectId: session.project?.id ?? null,
          reportedProjectName: session.project?.name ?? null,
          checkedInAt: capturedAtISO,
          held: !!records.held,
          // State B — why the worker is back. Null on a first check-in.
          reason: session.reCheckInReason ?? null,
        })
      }
      return {
        success: true,
        recordId,
        checkedInAt: capturedAtISO,
        syncStatus: 'saved',
        held: records.held,
      }
    },

    // FD-001
    async submitCheckOut(session, entry, checkedOutAtISO) {
      const records = buildCheckOutRecords(session, entry, checkedOutAtISO)
      appendLog({ recordId: entry.recordId ?? null, at: checkedOutAtISO, records })
      // Workday no longer active — release the demo duplicate guard.
      if (session.employee?.id) activeWorking.delete(session.employee.id)
      // BUILD 001 — close the workday fact for the Owner board. The check-in
      // time and the reported project are left exactly as captured.
      closeSegment(subjectOf(session), isoDate(new Date(entry.checkedInAt || checkedOutAtISO)), checkedOutAtISO)
      return {
        success: true,
        recordId: entry.recordId ?? `fixture://pending/${cryptoRandom()}`,
        checkedOutAt: checkedOutAtISO,
        syncStatus: 'saved',
        held: records.held,
      }
    },

    // FD-001
    async submitDayClose(session, entry, closedAtISO) {
      const records = buildDayCloseRecords(session, entry, closedAtISO)
      appendLog({ recordId: entry.recordId, at: closedAtISO, records })
      return { success: true, recordId: entry.recordId, closedAt: closedAtISO, syncStatus: 'saved' }
    },

    async submitPhotoNote(session, capturedAtISO, fileName) {
      const recordId = `fixture://evidence/${cryptoRandom()}`
      appendLog({ recordId, at: capturedAtISO, records: buildPhotoEvidence(session, capturedAtISO, fileName) })
      return { success: true, recordId, checkedInAt: capturedAtISO, syncStatus: 'saved' }
    },

    async submitIssue(session, capturedAtISO, text) {
      const recordId = `fixture://pending/${cryptoRandom()}`
      appendLog({ recordId, at: capturedAtISO, records: buildIssuePending(session, capturedAtISO, text) })
      return { success: true, recordId, checkedInAt: capturedAtISO, syncStatus: 'saved' }
    },
  }
}

// Company-code normalization: trim whitespace, case-fold. Kept identical to the
// server's normalization so demo and live behave the same.
function normalizeCode(code) {
  return String(code || '').trim().toUpperCase()
}

// Deterministic-enough id for demo logging without Math.random reliance issues.
function cryptoRandom() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  return 'x' + Date.now().toString(36)
}
